import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { FiX, FiSend, FiPhoneOff, FiMessageSquare } from "react-icons/fi";
import { db } from "../../firebase";
import {
  ref,
  push,
  onValue,
  query,
  orderByChild,
  set,
  update,
  serverTimestamp,
} from "firebase/database";
import logo from "/images/logo.svg";

const Chat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const scrollPosRef = useRef(window.scrollY);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [adminStatus, setAdminStatus] = useState("offline");
  const [chatStatus, setChatStatus] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [connectedAdmin, setConnectedAdmin] = useState(null);
  const [isChatVisible, setIsChatVisible] = useState(true);
  const [isDesktop, setIsDesktop] = useState(false);

  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const notificationAudio = useRef(null);

  const defaultTitle = "Hybrid Energy Rock";

  const getNotificationAudio = useCallback(() => {
    if (!notificationAudio.current) {
      notificationAudio.current = new Audio("/notification.mp3");
    }
    return notificationAudio.current;
  }, []);

  const toggleChat = useCallback(() => {
    if (!isOpen && !isDesktop) {
      scrollPosRef.current = window.scrollY;
      setIsOpen(true);
    } else if (isOpen && !isDesktop) {
      setIsOpen(false);
      setTimeout(() => {
        window.scrollTo(0, scrollPosRef.current);
      }, 100);
    } else {
      setIsOpen((prev) => !prev);
    }
  }, [isOpen, isDesktop]);

  const statusMessage = useMemo(() => {
    if (adminStatus === "online") {
      return "Online - Responding in real-time";
    } else if (adminStatus === "away") {
      return "Away - Response may be delayed";
    } else {
      return "Offline - Leave a message";
    }
  }, [adminStatus]);

  const chatContainerClass = useMemo(
    () =>
      isDesktop
        ? "fixed bottom-25 right-4 z-50 bg-white flex flex-col w-[360px] h-[500px] rounded-3xl shadow-xl"
        : "fixed inset-0 z-50 bg-white flex flex-col rounded-none",
    [isDesktop]
  );
  const headerClass = useMemo(
    () => (isDesktop ? "rounded-t-3xl" : ""),
    [isDesktop]
  );

  const messageMetadata = useMemo(() => {
    const userMessages = messages.filter((msg) => msg.sender === "user");
    const lastMsg = userMessages.length
      ? userMessages[userMessages.length - 1].id
      : null;
    const readMsgs = userMessages.filter((msg) => msg.status === "read");
    const lastRead = readMsgs.length ? readMsgs[readMsgs.length - 1].id : null;
    return { lastUserMessageId: lastMsg, lastReadMessageId: lastRead };
  }, [messages]);
  const generateChatId = useCallback(() => {
    const newRef = push(ref(db, "chats"));
    return newRef.key;
  }, []);

  const handleNewChat = useCallback(() => {
    localStorage.removeItem("chatId");
    setChatId(null);
    setMessages([]);
    setChatStatus(null);
    setInput("");
    setConnectedAdmin(null);
  }, []);

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.matchMedia("(min-width: 64rem)").matches);
    };
    checkDesktop();

    const mediaQueryList = window.matchMedia("(min-width: 64rem)");

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", checkDesktop);
      window.addEventListener("orientationchange", checkDesktop);
      return () => {
        mediaQueryList.removeEventListener("change", checkDesktop);
        window.removeEventListener("orientationchange", checkDesktop);
      };
    } else {
      mediaQueryList.addListener(checkDesktop);
      window.addEventListener("orientationchange", checkDesktop);
      return () => {
        mediaQueryList.removeListener(checkDesktop);
        window.removeEventListener("orientationchange", checkDesktop);
      };
    }
  }, []);

  useEffect(() => {
    if (isOpen && !isDesktop) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, isDesktop]);

  useEffect(() => {
    const storedChatId = localStorage.getItem("chatId");
    if (storedChatId) {
      const messagesRef = ref(db, `chats/${storedChatId}/messages`);
      onValue(
        messagesRef,
        (snapshot) => {
          if (snapshot.exists()) {
            setChatId(storedChatId);
          } else {
            localStorage.removeItem("chatId");
          }
        },
        { onlyOnce: true }
      );
    }
  }, []);

  useEffect(() => {
    const adminStatusRef = ref(db, "adminStatus");
    const activeAdminsRef = ref(db, "globalPresence/adminIds");

    const statusListeners = {};
    const currentStatuses = {};

    const updateAggregatedStatus = () => {
      const values = Object.values(currentStatuses);
      const hasOnline = values.includes("online");
      const hasAway = values.includes("away");
      if (hasOnline) setAdminStatus("online");
      else if (hasAway) setAdminStatus("away");
      else setAdminStatus("offline");
    };

    const unsubscribeGlobalPresence = onValue(activeAdminsRef, (snapshot) => {
      const activeAdmins = snapshot.val();

      Object.values(statusListeners).forEach((unsub) => unsub());
      Object.keys(statusListeners).forEach((key) => delete statusListeners[key]);
      Object.keys(currentStatuses).forEach((key) => delete currentStatuses[key]);

      if (!activeAdmins || Object.keys(activeAdmins).length === 0) {
        setAdminStatus("offline");
        return;
      }

      const adminIds = Object.keys(activeAdmins);

      adminIds.forEach((id) => {
        const statusRef = ref(db, `admins/${id}/globalStatus`);
        statusListeners[id] = onValue(statusRef, (statusSnap) => {
          currentStatuses[id] = statusSnap.val() || "offline";
          updateAggregatedStatus();
        });
      });
    });

    const unsubscribeAdminStatus = onValue(adminStatusRef, (snapshot) => {
      if (snapshot.val() === true && adminStatus === "offline") {
        setAdminStatus("online");
      }
    });

    return () => {
      unsubscribeGlobalPresence();
      unsubscribeAdminStatus();
      Object.values(statusListeners).forEach((unsub) => unsub());
    };
  }, [adminStatus]);

  useEffect(() => {
    if (!chatId) return;

    const messagesRef = ref(db, `chats/${chatId}/messages`);
    const messagesQuery = query(messagesRef, orderByChild("timestamp"));
    const typingRef = ref(db, `chats/${chatId}/typing`);
    const statusRef = ref(db, `chats/${chatId}/status`);
    const chatAdminRef = ref(db, `chats/${chatId}/connectedAdmin/name`);

    const unsubscribeMessages = onValue(messagesQuery, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const messagesList = Object.entries(data)
          .map(([id, msg]) => ({ id, ...msg }))
          .sort((a, b) => a.timestamp - b.timestamp);

        setMessages(messagesList);

        const unreadAdminMessages = messagesList.filter(
          (msg) => msg.sender === "admin" && msg.status !== "read"
        );

        const count = unreadAdminMessages.length;
        setUnreadCount(count);
        if (count > 0 && isChatVisible) {
          getNotificationAudio()
            .play()
            .catch(() => {});
        }
      } else {
        setMessages([]);
      }
    });

    const unsubscribeTyping = onValue(typingRef, (snapshot) => {
      const typingData = snapshot.val() || {};
      setIsTyping(typingData.admin === true);
    });

    const unsubscribeStatus = onValue(statusRef, (snapshot) => {
      setChatStatus(snapshot.val());
    });

    const unsubscribeAdmin = onValue(chatAdminRef, (snapshot) => {
      setConnectedAdmin(snapshot.val());
    });

    return () => {
      unsubscribeMessages();
      unsubscribeTyping();
      unsubscribeStatus();
      unsubscribeAdmin();
    };
  }, [chatId, isChatVisible, getNotificationAudio]);

  useEffect(() => {
    const userOnlineRef =
      chatId && messages.length > 0
        ? ref(db, `chats/${chatId}/userOnline`)
        : null;

    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsChatVisible(isVisible);

      document.title = isVisible
        ? defaultTitle
        : unreadCount > 0
          ? `(${unreadCount}) New Message${unreadCount > 1 ? "s" : ""}`
          : defaultTitle;

      if (userOnlineRef) {
        set(userOnlineRef, isVisible);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    handleVisibilityChange();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (userOnlineRef) {
        set(userOnlineRef, false);
      }
    };
  }, [chatId, messages.length, unreadCount]);

  useEffect(() => {
    if (!chatId || !isOpen || !isChatVisible) return;

    const unreadMessages = messages.filter(
      (msg) => msg.sender === "admin" && msg.status !== "read"
    );

    unreadMessages.forEach((msg) => {
      const msgRef = ref(db, `chats/${chatId}/messages/${msg.id}`);
      update(msgRef, { status: "read" });
    });
  }, [messages, chatId, isOpen, isChatVisible]);

  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [isOpen, messages]);
  useEffect(() => {
    if (chatStatus?.state === "ended" && chatStatus.endedAt) {
      let endedTime;
      if (chatStatus.endedAt.seconds) {
        endedTime = chatStatus.endedAt.seconds * 1000;
      } else {
        endedTime = chatStatus.endedAt;
      }

      const elapsed = Date.now() - endedTime;
      const THIRTY_MINUTES = 30 * 60 * 1000;

      if (elapsed >= THIRTY_MINUTES) {
        handleNewChat();
      } else {
        const timeout = setTimeout(() => {
          handleNewChat();
        }, THIRTY_MINUTES - elapsed);
        return () => clearTimeout(timeout);
      }
    }
  }, [chatStatus, handleNewChat]);

  const handleSendMessage = useCallback(
    async (e) => {
      e.preventDefault();

      if (!input.trim() || (chatStatus && chatStatus.state === "ended")) return;

      let currentChatId = chatId;
      if (!currentChatId) {
        currentChatId = generateChatId();
        localStorage.setItem("chatId", currentChatId);
        setChatId(currentChatId);
      }

      const timestamp = Date.now();

      if (messages.length === 0) {
        const adminMessage = {
          text: "Hi there!\nWelcome to Hybrid Energy Rock.\nHow can we help you?",
          sender: "admin",
          timestamp,
          status: "read",
        };
        await push(ref(db, `chats/${currentChatId}/messages`), adminMessage);
      }

      const userMessage = {
        text: input,
        sender: "user",
        timestamp,
        status: "delivered",
      };

      const newMessageRef = await push(
        ref(db, `chats/${currentChatId}/messages`),
        userMessage
      );

      const statusRef = ref(db, `chats/${currentChatId}/status`);
      set(statusRef, { state: "active", updatedAt: serverTimestamp() });

      setInput("");
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      setTimeout(() => {
        update(newMessageRef, { status: "delivered" });
      }, 2000);
    },
    [input, chatStatus, chatId, messages.length, generateChatId]
  );
  const handleEndChat = useCallback(() => {
    if (!chatId) return;
    const statusRef = ref(db, `chats/${chatId}/status`);
    set(ref(db, `chats/${chatId}/ended`), true);
    set(statusRef, {
      state: "ended",
      updatedAt: serverTimestamp(),
      endedAt: serverTimestamp(),
    });
  }, [chatId]);

  const handleTyping = useCallback(() => {
    if (!chatId) return;
    const typingRef = ref(db, `chats/${chatId}/typing`);
    update(typingRef, { user: true, admin: false });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      update(typingRef, { user: false, admin: false });
    }, 3000);
  }, [chatId]);

  const handleTextareaChange = useCallback(
    (e) => {
      setInput(e.target.value);
      handleTyping();
      e.target.style.height = "auto";
      const maxHeight = 96;
      e.target.style.height = `${Math.min(e.target.scrollHeight, maxHeight)}px`;
    },
    [handleTyping]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.shiftKey && e.key === "Enter") {
        e.preventDefault();
        const { selectionStart, selectionEnd } = e.target;
        const newValue =
          input.substring(0, selectionStart) +
          "\n" +
          input.substring(selectionEnd);
        setInput(newValue);
      } else if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage(e);
      }
    },
    [input, handleSendMessage]
  );

  const { lastUserMessageId, lastReadMessageId } = messageMetadata;

  return (
    <>
      {isOpen && (
        <div
          className={chatContainerClass}
          style={{
            position: "fixed",
            zIndex: 2147483647,
            filter: "none",
          }}
        >
          <div
            className={`bg-linear-to-r from-blue-600 to-indigo-600 text-white p-4 shrink-0 shadow-lg ${headerClass}`}
          >
            <div
              className="flex items-center justify-between mb-2"
              id="safePad"
            >
              <div className="flex items-center gap-3">
                <div className="relative w-10 h-10 overflow-hidden transition-transform duration-300 hover:scale-105">
                  <img
                    src={logo}
                    alt="Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-medium tracking-wide">
                    {connectedAdmin
                      ? `Chat with ${connectedAdmin}`
                      : "Customer Service"}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleEndChat}
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-200 cursor-pointer"
                >
                  <FiPhoneOff size={18} className="text-white" />
                </button>

                {!isDesktop && (
                  <button
                    onClick={toggleChat}
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-200"
                  >
                    <FiX size={18} className="text-white" />
                  </button>
                )}
              </div>
            </div>

            <p
              className="text-xs text-white/80 font-light flex items-center gap-2"
              id="safePad"
            >
              <span
                className={`inline-block w-2.5 h-2.5 rounded-full ${
                  adminStatus === "online"
                    ? "bg-green-400"
                    : adminStatus === "away"
                      ? "bg-yellow-400"
                      : "bg-red-400"
                }`}
              ></span>
              {statusMessage}
            </p>
          </div>

          <div
            className={`flex-1 overflow-y-auto p-4 ${chatStatus?.state === "ended" ? "h-2/3" : "h-[calc(100%-180px)]"}`}
            id="safePad"
          >
            {messages.length === 0 ? (
              <div className="mb-4 flex justify-start">
                <div className="px-4 py-3 rounded-2xl max-w-[75%] break-words whitespace-normal bg-gray-100 text-gray-800 shadow-xs">
                  <div className="text-sm">
                    Hi there!
                    <br />
                    Welcome to Hybrid Energy Rock.
                    <br />
                    How can we help you?
                  </div>
                </div>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`mb-4 flex flex-col ${msg.sender === "user" ? "items-end" : "items-start"}`}
                >
                  <div
                    className={`px-4 py-3 rounded-2xl max-w-[75%] break-words whitespace-pre-line ${
                      msg.sender === "user"
                        ? "bg-linear-to-br from-blue-500 to-indigo-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-800 shadow-xs"
                    }`}
                  >
                    <div className="text-sm">{msg.text}</div>
                  </div>

                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    {msg.sender === "user" && msg.status && (
                      <span className="text-xs font-medium">
                        {msg.id === lastUserMessageId && msg.status !== "read"
                          ? msg.status.charAt(0).toUpperCase() +
                            msg.status.slice(1)
                          : msg.id === lastReadMessageId
                            ? "Read"
                            : ""}
                      </span>
                    )}

                    {!(
                      msg.sender === "admin" &&
                      msg.text ===
                        "Hi there!\nWelcome to Hybrid Energy Rock.\nHow can we help you?"
                    ) && (
                      <span>
                        {new Date(msg.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}

            <div ref={messagesEndRef} />

            {isTyping && connectedAdmin && (
              <div className="mb-4 flex justify-start">
                <div className="px-4 py-3 rounded-2xl max-w-[75%] bg-gray-100 text-gray-800 shadow-xs flex items-center">
                  <div className="flex space-x-1">
                    <div
                      className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    />
                    <div
                      className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    />
                    <div
                      className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className={`border-t border-gray-200 shrink-0 ${
              chatStatus?.state === "ended" ? "h-1/3" : "h-auto min-h-20"
            }`}
            id="safePad"
          >
            {chatStatus && chatStatus.state === "ended" ? (
              <div className="w-full h-full p-6 flex flex-col justify-center items-center text-center text-gray-500 space-y-4">
                <p className="text-sm">
                  This chat has ended. You can view the chat history for the
                  next 30 minutes.
                </p>
                <button
                  onClick={handleNewChat}
                  className="px-6 py-2 bg-linear-to-br from-blue-500 to-indigo-600 text-white rounded-full hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 ease-in-out shadow-md"
                >
                  Start New Chat
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 w-full h-full p-3">
                <textarea
                  placeholder="Enter your message..."
                  value={input}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  className="flex-grow p-3 bg-gray-100 text-gray-800 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-blue-500 resize-none overflow-y-auto"
                  rows={1}
                  style={{ maxHeight: "96px", lineHeight: "24px" }}
                  ref={textareaRef}
                />
                <button
                  type="submit"
                  className="w-10 h-10 rounded-full bg-linear-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 ease-in-out shadow-md cursor-pointer"
                  onClick={handleSendMessage}
                >
                  <FiSend size={18} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {(!isOpen || (isOpen && isDesktop)) && (
        <button
          onClick={toggleChat}
          style={{
            position: "fixed",
            zIndex: 2147483647,
            filter: "none",
          }}
          className="fixed bottom-6 right-6 w-14 h-14 text-white rounded-full shadow-xl bg-linear-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-70 cursor-pointer"
        >
          <div className="relative flex items-center justify-center w-full h-full">
            {isOpen ? (
              <FiX size={28} className="text-white" />
            ) : (
              <FiMessageSquare size={28} className="text-white" />
            )}

            {!isOpen && unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-medium w-5 h-5 rounded-full flex items-center justify-center shadow-md">
                {unreadCount}
              </span>
            )}
          </div>
        </button>
      )}
    </>
  );
};

export default Chat;
